import {
  // Static import map — listed individually so Vite's bundle
  // tree-shakes the rest of lucide-react out. Add a new entry
  // here when a new badge ships with a previously-unused icon
  // name; the seed's icon_name field is the canonical source.
  Award, Atom, BadgeDollarSign, Battery, BatteryCharging, BatteryFull,
  BatteryMedium, Bolt, Building2, Cake, Calendar, CalendarCheck,
  CalendarDays, CalendarHeart, Clock, Compass, Crown, Diamond,
  DollarSign, DoorOpen, Flame, FlaskConical, Gem, Globe, Hammer,
  Heart, Home, Hourglass, Leaf, Lock, Map, MapPin, Medal, Megaphone,
  Moon, Network, PartyPopper, Plug, Rocket, ShoppingBag, Sparkles,
  Star, Sun, Sunrise, Timer, Trophy, UserPlus, Users, Wind, Zap,
  type LucideIcon,
} from 'lucide-react';
import styles from './BadgeIcon.module.css';

/**
 * Color-tier system for badge icon circles. Four tiers escalate
 * from "first earn" feel to "mastery." Tier 4 carries a subtle
 * gold accent (outer ring) so the rarest badges read as distinct
 * even at small sizes.
 *
 * The CSS sources `--evi-accent` (defaults to the 142°/71% green
 * the brand uses) so themed skins shift the badges in lock-step
 * with the rest of the PWA. Tier-specific lightness/saturation
 * adjustments are baked into the module CSS.
 */
export type BadgeColorTier = 1 | 2 | 3 | 4;

export interface BadgeIconProps {
  /** Lucide-react component name in PascalCase (matches the seed
   *  field `icon_name`). Unrecognized names render the Award icon
   *  so a typo or future-named badge doesn't blank the grid. */
  iconName?: string;
  /** Color tier 1-4. Defaults to 1. */
  colorTier?: BadgeColorTier;
  /** Pixel diameter of the surrounding circle. Common values:
   *  32 (compact list), 48, 64 (default; rewards page grid), 96
   *  (hero / earn-toast). */
  size?: number;
}

/** Static registry — every Lucide name the seed currently uses,
 *  plus a few extras for future badges. Add new entries here AND
 *  the import list above when a fresh icon name lands.
 *
 *  Why static + manual rather than dynamic / icons-namespace:
 *    - lucide-react@0.468 doesn't export an `icons` namespace.
 *    - Tree-shaking keeps the bundle slim; a `Record<string, any>`
 *      built off `import * as Lucide` would pull every icon.
 *    - Future-proofing: when AI-generated badges land we swap the
 *      whole component for an <img src={icon_url}>, not this map. */
const BADGE_ICONS: Record<string, LucideIcon> = {
  Award, Atom, BadgeDollarSign, Battery, BatteryCharging, BatteryFull,
  BatteryMedium, Bolt, Building2, Cake, Calendar, CalendarCheck,
  CalendarDays, CalendarHeart, Clock, Compass, Crown, Diamond,
  DollarSign, DoorOpen, Flame, FlaskConical, Gem, Globe, Hammer,
  Heart, Home, Hourglass, Leaf, Lock, Map, MapPin, Medal, Megaphone,
  Moon, Network, PartyPopper, Plug, Rocket, ShoppingBag, Sparkles,
  Star, Sun, Sunrise, Timer, Trophy, UserPlus, Users, Wind, Zap,
};

// noUncheckedIndexedAccess narrows CSS-module class lookups to
// `string | undefined`. The module always defines these classes,
// so the cast is correct at runtime — it just satisfies the
// Record<BadgeColorTier, string> shape the lookup site needs.
const TIER_CLASS: Record<BadgeColorTier, string> = {
  1: styles.tier1 as string,
  2: styles.tier2 as string,
  3: styles.tier3 as string,
  4: styles.tier4 as string,
};

export function BadgeIcon({
  iconName,
  colorTier = 1,
  size = 64,
}: BadgeIconProps) {
  const Icon: LucideIcon = (iconName ? BADGE_ICONS[iconName] : undefined) ?? Award;
  // Inner stroke gets ~50% of the circle diameter — keeps the
  // glyph proportional across 32/48/64/96 sizes.
  const iconSize = Math.round(size * 0.48);
  return (
    <span
      className={`${styles.circle} ${TIER_CLASS[colorTier]}`}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <Icon size={iconSize} strokeWidth={2.25} />
    </span>
  );
}
